import { translate } from "@/i18n/I18nProvider";
import { ARMOR_SLOT_ORDER, OUTFIT_ARMOR_SLOT } from "./vipArmorSlots";
import { getCatalogEntry } from "./vipSimulatorCatalog";

export { ARMOR_SLOT_ORDER, OUTFIT_ARMOR_SLOT };

/**
 * @param {{ name?: string, cat?: string, id?: string } | null | undefined} entry
 */
export function isOutfitEntry(entry) {
    if (!entry) return false;
    if (entry.cat === "outfit") return true;
    const name = String(entry.name || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    return /^traje\b/.test(name);
}

/**
 * @param {{ cat?: string } | null | undefined} entry
 */
export function isArmorClothingEntry(entry) {
    if (!entry?.cat) return false;
    return entry.cat.startsWith("armor-");
}

/**
 * @param {{ inv: unknown[], hotbar: unknown[], armor: Record<string, unknown> }} sim
 */
export function hasOutfitEquippedInArmor(sim) {
    for (const p of Object.values(sim.armor)) {
        if (!p) continue;
        if (isOutfitEntry(getCatalogEntry(p.catalogId))) return true;
    }
    return false;
}

/**
 * @param {{ armor: Record<string, unknown> }} sim
 */
export function hasArmorClothingInArmor(sim) {
    for (const p of Object.values(sim.armor)) {
        if (!p) continue;
        const entry = getCatalogEntry(p.catalogId);
        if (isArmorClothingEntry(entry) && !isOutfitEntry(entry)) return true;
    }
    return false;
}

/** Todos os 7 slots de equipamento ocupados. */
export function allArmorSlotsFull(sim) {
    return ARMOR_SLOT_ORDER.every((accept) => Boolean(sim.armor[accept]));
}

/**
 * Primeiro slot de equipamento livre (ordem Rust). Prefere o slot sugerido pelo catálogo se estiver vazio.
 * @param {{ armor: Record<string, unknown> }} sim
 * @param {{ cat?: string } | null | undefined} entry
 * @returns {string | null}
 */
export function findFirstFreeArmorSlot(sim, entry) {
    if (!entry) return null;
    const preferred =
        entry.cat && entry.cat.startsWith("armor-") && ARMOR_SLOT_ORDER.includes(entry.cat)
            ? entry.cat
            : null;

    if (preferred && !sim.armor[preferred] && canPlaceClothingInSlot(sim, "armor", preferred, entry)) {
        return preferred;
    }

    for (const accept of ARMOR_SLOT_ORDER) {
        if (!sim.armor[accept] && canPlaceClothingInSlot(sim, "armor", accept, entry)) {
            return accept;
        }
    }
    return null;
}

/**
 * @param {{ inv: unknown[], hotbar: unknown[], armor: Record<string, unknown> }} sim
 * @param {"inv"|"hotbar"|"armor"} zone
 * @param {string | undefined} accept
 * @param {{ cat?: string, name?: string } | null | undefined} entry
 */
export function canPlaceClothingInSlot(sim, zone, accept, entry) {
    if (!entry) return false;

    if (isArmorClothingEntry(entry)) {
        if (zone !== "armor") return false;
        if (hasOutfitEquippedInArmor(sim)) return false;
        if (!accept || !ARMOR_SLOT_ORDER.includes(accept)) return false;
        return !sim.armor[accept];
    }

    if (isOutfitEntry(entry)) {
        if (zone === "inv" || zone === "hotbar") return true;
        if (zone === "armor") {
            if (accept !== OUTFIT_ARMOR_SLOT) return false;
            if (hasArmorClothingInArmor(sim)) return false;
            return true;
        }
        return false;
    }

    return true;
}

/**
 * @param {{ inv: unknown[], hotbar: unknown[], armor: Record<string, unknown> }} sim
 * @param {"inv"|"hotbar"|"armor"} zone
 * @param {string | undefined} accept
 * @param {{ cat?: string, name?: string } | null | undefined} entry
 * @returns {string | null}
 */
export function getClothingPlacementBlockReason(sim, zone, accept, entry) {
    if (!entry) return null;
    if (canPlaceClothingInSlot(sim, zone, accept, entry)) return null;

    if (isArmorClothingEntry(entry)) {
        if (zone !== "armor") {
            return translate("simulator.clothing.onlyWearable");
        }
        if (hasOutfitEquippedInArmor(sim)) {
            return translate("simulator.place.outfitEquipped");
        }
        if (allArmorSlotsFull(sim)) {
            return translate("simulator.clothing.armorFull");
        }
        if (sim.armor[accept]) {
            return translate("simulator.clothing.slotTaken");
        }
        return translate("simulator.clothing.cannotPlace");
    }

    if (isOutfitEntry(entry)) {
        if (zone === "armor" && hasArmorClothingInArmor(sim)) {
            return translate("simulator.clothing.removeOthers");
        }
        if (zone === "armor") {
            return translate("simulator.clothing.outfitTorsoOnly");
        }
        return translate("simulator.clothing.outfitBadSlot");
    }

    return null;
}
